import {
  SlopedRoofTypeTypes,
  SlopedRoofInsulationTypeTypes,
} from "@iko-design-center/shared"
import { useHistory } from "react-router"
import { useRoutes } from "../routes/hooks"
import { Models } from "../../components/viewer/renderer.api"
import { useApplication } from "../application/hooks"
import { SlopedRoofData } from "../sloped-roof/store"
import { useSlopedRoof } from "../sloped-roof/hooks"
import { checkValidQueriedValueOfType, getEnumKey } from "./query-helpers"
import { useTranslation } from "react-i18next"

export function useQueryParams() {
  const { setCurrentModel } = useApplication()
  const { t } = useTranslation() as any
  const { Routes } = useRoutes()
  const history = useHistory()

  const {
    setSlopedRoofTypeType,
    setSlopedRoofInsulationType,
    setSlopedRoofInsulationTypeWithThickness,
  } = useSlopedRoof()

  const setQueryParams = (
    data: SlopedRoofData,
    language: string,
    redirect = false
  ) => {
    const languageRoute = t("routes.SLOPED_ROOF_3_OVERVIEW", { lng: language })
    const urlWithoutParams = `${window.location.origin}${languageRoute}`
    const url = new URL(urlWithoutParams ?? window.location.href)

    // Roof type
    const roofType = data.roofType.type
    const roofTypeEnum = getEnumKey(roofType, SlopedRoofTypeTypes)
    if (roofTypeEnum) {
      url.searchParams.append("roof-type", roofTypeEnum)
    }

    // Roof insulation
    const roofInsulation = data.roofInsulation.type
    const roofInsulationEnum = getEnumKey(
      roofInsulation,
      SlopedRoofInsulationTypeTypes
    )
    if (roofInsulationEnum) {
      url.searchParams.append("roof-insulation", roofInsulationEnum)

      // Include roof insulation thickness
      const roofInsulationThermalThickness =
        data.thermalPerformance.roofInsulation.thickness
      if (roofInsulationThermalThickness) {
        url.searchParams.append(
          "roof-insulation-thickness",
          roofInsulationThermalThickness.toString()
        )
      }
    }

    if (redirect) {
      window.location.href = url.href
    } else {
      window.history.replaceState([], document.title, url.href as string)
    }
  }

  const redirectBasedOnQueryParams = () => {
    const url = new URL(window.location.href)
    const query = {
      roofType: url.searchParams.get("roof-type"),
      roofInsulation: url.searchParams.get("roof-insulation"),
      roofInsulationThickness: url.searchParams.get(
        "roof-insulation-thickness"
      ),
      detail: url.searchParams.get("detail"),
      token: url.searchParams.get("token"),
    }

    // Roof Type
    const validRoofType = checkValidQueriedValueOfType(
      query.roofType,
      SlopedRoofTypeTypes
    )
    if (validRoofType) setSlopedRoofTypeType(validRoofType)

    // Roof Insulation
    const validRoofInsulation = checkValidQueriedValueOfType(
      query.roofInsulation,
      SlopedRoofInsulationTypeTypes
    )
    if (validRoofInsulation) {
      // Include roof insulation thickness
      if (query.roofInsulationThickness) {
        setSlopedRoofInsulationTypeWithThickness(
          validRoofInsulation,
          query.roofInsulationThickness
        )
      } else {
        setSlopedRoofInsulationType(validRoofInsulation)
      }
    }

    // Model
    const validModel = checkValidQueriedValueOfType(query.detail, Models)
    if (validModel) setCurrentModel(validModel)
    else setCurrentModel(Models.SLOPED_ROOF)

    // Routing
    let route = Routes.SLOPED_ROOF_1_ROOF_TYPE
    if (validRoofType && validRoofInsulation) {
      route = Routes.SLOPED_ROOF_3_OVERVIEW
    } else if (validRoofType) {
      route = Routes.SLOPED_ROOF_2_ROOF_INSULATION
    }

    if (query.token) {
      history.push({
        pathname: route as any,
        search: `?token=${query.token}`,
      })
    } else {
      history.push(route as any)
    }
  }

  return { setQueryParams, redirectBasedOnQueryParams }
}
